import {
  Timestamp,
  doc,
  type DocumentData,
  type DocumentReference,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import type { ActivityComment } from '@domain/models/activity-comments'

const USERS_COLLECTION = 'users'
const ACTIVITIES_EXPLORE_COLLECTION = 'activities_explore'
const WEBINARS_COLLECTION = 'webinars'
const PODCAST_EPISODES_COLLECTION = 'podcast_episodes'
const INSTAGRAM_REELS_COLLECTION = 'instagram_reels'
const WEBINAR_SUGGESTIONS_COLLECTION = 'webinar_suggestions'
const PD_SYMPTOMS_COLLECTION = 'pd_symptoms'
const WHAT_WORKS_COLLECTION = 'what_works'

function getCollectionRef(
  collectionName: string,
  id: string,
): DocumentReference<DocumentData> {
  if (!firestore) {
    throw new Error('Firestore is not initialized.')
  }
  return doc(firestore, collectionName, id)
}

function isReferenceLike(value: unknown): value is DocumentReference<DocumentData> {
  if (!value || typeof value !== 'object') {
    return false
  }
  if (!('id' in value) || !('path' in value)) {
    return false
  }

  const maybeRef = value as { id?: unknown; path?: unknown }
  return typeof maybeRef.id === 'string' && typeof maybeRef.path === 'string'
}

function refToId(value: unknown): string | undefined {
  if (!isReferenceLike(value)) {
    return undefined
  }
  return value.id
}

function refsToIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  return value
    .map((item) => refToId(item))
    .filter((item): item is string => Boolean(item))
}

function idsToRefs(
  ids: string[] | undefined,
  collectionName: string,
): DocumentReference<DocumentData>[] | undefined {
  if (ids === undefined) {
    return undefined
  }
  return ids
    .map((id) => id.trim())
    .filter((id) => id.length > 0)
    .map((id) => getCollectionRef(collectionName, id))
}

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

function stripNullish(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null),
  )
}

function parseSolutionRef(
  solutionRef: unknown,
): { pdSymptomId?: string; solutionId?: string } {
  if (!isReferenceLike(solutionRef)) {
    return {}
  }

  const segments = solutionRef.path.split('/')
  if (segments.length < 4) {
    return {}
  }

  return {
    pdSymptomId: segments[1],
    solutionId: segments[3],
  }
}

function parseActivityRef(
  activityRef: unknown,
): {
  activityRef?: string
  activityRefCollection?:
    | 'activities_explore'
    | 'webinars'
    | 'podcast_episodes'
    | 'instagram_reels'
    | 'webinar_suggestions'
} {
  if (!isReferenceLike(activityRef)) {
    return {}
  }

  const segments = activityRef.path.split('/')
  if (segments.length < 2) {
    return {}
  }

  const collectionName = segments[0]
  const itemId = segments[1]
  if (!itemId) {
    return {}
  }

  if (collectionName === ACTIVITIES_EXPLORE_COLLECTION) {
    return {
      activityRef: itemId,
      activityRefCollection: ACTIVITIES_EXPLORE_COLLECTION,
    }
  }

  if (collectionName === WEBINARS_COLLECTION) {
    return {
      activityRef: itemId,
      activityRefCollection: WEBINARS_COLLECTION,
    }
  }

  if (collectionName === PODCAST_EPISODES_COLLECTION) {
    return {
      activityRef: itemId,
      activityRefCollection: PODCAST_EPISODES_COLLECTION,
    }
  }

  if (collectionName === INSTAGRAM_REELS_COLLECTION) {
    return {
      activityRef: itemId,
      activityRefCollection: INSTAGRAM_REELS_COLLECTION,
    }
  }

  if (collectionName === WEBINAR_SUGGESTIONS_COLLECTION) {
    return {
      activityRef: itemId,
      activityRefCollection: WEBINAR_SUGGESTIONS_COLLECTION,
    }
  }

  return {
    activityRef: itemId,
  }
}

export class ActivityCommentConverter {
  public static fromFirestore(
    uid: string,
    data: Record<string, unknown>,
  ): ActivityComment {
    const parsedSolutionRef = parseSolutionRef(data['solution_ref'])
    const parsedActivityRef = parseActivityRef(data['activity_ref'])

    return {
      uid,
      comment: typeof data['comment'] === 'string' ? data['comment'] : undefined,
      postedOn: toOptionalDate(data['posted_on']),
      postedBy: refToId(data['posted_by']),
      activityRef: parsedActivityRef.activityRef,
      activityRefCollection: parsedActivityRef.activityRefCollection,
      routineRef: refToId(data['routine_ref']),
      pdSymptomId: parsedSolutionRef.pdSymptomId,
      solutionRef: parsedSolutionRef.solutionId,
      likes: typeof data['likes'] === 'number' ? data['likes'] : undefined,
      likedBy: refsToIds(data['liked_by']),
    }
  }

  public static toFirestore(
    comment: Partial<ActivityComment>,
  ): Record<string, unknown> {
    const activityRefCollection = comment.activityRefCollection ?? ACTIVITIES_EXPLORE_COLLECTION

    const payload: Record<string, unknown> = {
      comment: comment.comment,
      posted_on: comment.postedOn
        ? Timestamp.fromDate(comment.postedOn)
        : undefined,
      posted_by: comment.postedBy
        ? getCollectionRef(USERS_COLLECTION, comment.postedBy)
        : undefined,
      activity_ref: comment.activityRef
        ? getCollectionRef(activityRefCollection, comment.activityRef)
        : undefined,
      routine_ref: comment.routineRef
        ? getCollectionRef(USERS_COLLECTION, comment.routineRef)
        : undefined,
      likes: comment.likes,
      liked_by: idsToRefs(comment.likedBy, USERS_COLLECTION),
    }

    if (comment.pdSymptomId && comment.solutionRef) {
      payload.solution_ref = getCollectionRef(
        `${PD_SYMPTOMS_COLLECTION}/${comment.pdSymptomId}/${WHAT_WORKS_COLLECTION}`,
        comment.solutionRef,
      )
    }

    return stripNullish(payload)
  }
}
